import {
	Box,
	Drawer,
	IconButton,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import { t } from "i18next";
import { Suspense, useId } from "react";
import { type HelpKey, getHelpEntry } from "../help-registry";
import { Flex } from "@/components/layout/flex/flex";
import { MAX_DRAWER_WIDTH } from "@/constants/drawers";
import { ICON_SIZES } from "@/constants/icons";

interface HelpDrawerProps {
	helpKey: HelpKey;
	open: boolean;
	onClose: () => void;
}

/**
 * A help entry's long explanation, from the right: full width on phones, the
 * standard drawer width otherwise. Opened over an Update drawer it stacks on
 * top, and the Update drawer stays mounted underneath with its edits.
 */
export function HelpDrawer({ helpKey, open, onClose }: HelpDrawerProps) {
	const titleId = useId();
	const { title, Guide } = getHelpEntry(helpKey);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			slotProps={{
				paper: {
					role: "dialog",
					"aria-labelledby": titleId,
					sx: { width: { xs: "100%", sm: MAX_DRAWER_WIDTH } },
				},
			}}
		>
			<Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
				<Flex justifyContent="space-between" alignItems="center" gap={1}>
					<Typography id={titleId} variant="h6" component="h2">
						{title()}
					</Typography>
					<IconButton aria-label={t("Close")} onClick={onClose} size="small">
						<XIcon size={ICON_SIZES.small} />
					</IconButton>
				</Flex>
			</Box>
			<Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
				{Guide && (
					<Suspense fallback={<HelpDrawerSkeleton />}>
						<Guide />
					</Suspense>
				)}
			</Box>
		</Drawer>
	);
}

function HelpDrawerSkeleton() {
	return (
		<Stack spacing={1}>
			<Skeleton variant="text" width="80%" />
			<Skeleton variant="text" width="90%" />
			<Skeleton variant="rounded" height={96} />
		</Stack>
	);
}
